import type { ReactElement } from 'react';
import { Provider } from 'react-redux';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import { AppConfigProvider } from '@/app/appConfig';
import { SessionProvider } from '@/app/session';
import { store } from '@/app/store';
import { ToastProvider } from '@/design/Toast';
import { AppFrame } from '@/layout/AppFrame';
import { NotFoundPage } from '@/features/sections';
import { BookingDetailPage } from '@/features/counter/BookingDetailPage';
import { BookingFormPage } from '@/features/counter/BookingFormPage';
import { BookingsPage } from '@/features/counter/BookingsPage';
import { CalendarPage } from '@/features/counter/CalendarPage';
import { CustomersPage } from '@/features/crm/CustomersPage';
import { DuesPage } from '@/features/money/DuesPage';
import { InvoicesPage } from '@/features/money/InvoicesPage';
import { RevenuePage } from '@/features/money/RevenuePage';
import { AccountPage } from '@/features/settings/AccountPage';
import { SettingsPage } from '@/features/settings/SettingsPage';
import { EnquiriesPage } from '@/features/counter/EnquiriesPage';
import { DriverFormPage } from '@/features/fleet/DriverFormPage';
import { DriversPage } from '@/features/fleet/DriversPage';
import { VehicleFormPage } from '@/features/fleet/VehicleFormPage';
import { VehiclesPage } from '@/features/fleet/VehiclesPage';
import { RateCardFormPage } from '@/features/pricing/RateCardFormPage';
import { RatesPage } from '@/features/pricing/RatesPage';
import { TodayPage } from '@/features/today/TodayPage';

/**
 * Routes match the navigation one for one.
 *
 * A role that cannot reach a section has no link to it, and the API refuses the
 * request anyway — the routes themselves are not the boundary. Typing the URL
 * for a screen you cannot use gets you the screen and an error from the server,
 * which is the honest outcome.
 */
export function App(): ReactElement {
  return (
    <Provider store={store}>
      <AppConfigProvider>
        {/* Outside the router, so a toast raised just before a navigation is
            still on screen once the next page has drawn. */}
        <ToastProvider>
        <SessionProvider>
          <BrowserRouter>
            <Routes>
              <Route element={<AppFrame />}>
                <Route index element={<TodayPage />} />
                <Route path="enquiries" element={<EnquiriesPage />} />
                <Route path="bookings" element={<BookingsPage />} />
                <Route path="bookings/new" element={<BookingFormPage />} />
                <Route path="bookings/:id" element={<BookingDetailPage />} />
                <Route path="bookings/:id/edit" element={<BookingFormPage />} />
                <Route path="calendar" element={<CalendarPage />} />
                <Route path="customers" element={<CustomersPage />} />

                <Route path="fleet" element={<VehiclesPage />} />
                <Route path="fleet/new" element={<VehicleFormPage />} />
                <Route path="fleet/:id" element={<VehicleFormPage />} />

                <Route path="drivers" element={<DriversPage />} />
                <Route path="drivers/new" element={<DriverFormPage />} />
                <Route path="drivers/:id" element={<DriverFormPage />} />

                <Route path="rates" element={<RatesPage />} />
                <Route path="rates/new" element={<RateCardFormPage />} />
                <Route path="rates/:id" element={<RateCardFormPage />} />

                {/* The Money section. Blocked until the tables behind it
                    existed; now they do. */}
                <Route path="invoices" element={<InvoicesPage />} />
                <Route path="dues" element={<DuesPage />} />
                <Route path="revenue" element={<RevenuePage />} />
                <Route path="settings" element={<SettingsPage />} />
                {/* No capability: everyone who signs in can change their own password. */}
                <Route path="account" element={<AccountPage />} />
                <Route path="*" element={<NotFoundPage />} />
              </Route>
            </Routes>
          </BrowserRouter>
        </SessionProvider>
        </ToastProvider>
      </AppConfigProvider>
    </Provider>
  );
}
